import React from 'react';
import PinGolfScore from './PinGolfScore';
import { markCountedWeeks, formatHandicap as formatHandicapText } from '../utils/pingolfScoring';
import './PinGolfMyWeeks.css';

// One player's season, week by week. The standings table says where everyone stands; this says
// how THIS player got there - and it is the only screen that makes a handicap explainable,
// since the index is the running mean of the finished cards in the Card column below. An
// unfinished week settles at a penalty per unplayed hole for its place and points, but is left
// out of the index (see recordCardResultForHandicap's countsTowardIndex).
//
// Every week of the season is listed, played or not, so a missed week reads as a gap rather
// than disappearing from the history.

const STATUS_LABELS = {
  scheduled: 'Not scheduled yet',
  open: 'Not played',
  in_progress: 'In progress',
  live: 'Live'
};

// Shown as the strokes applied to the card, not the raw average - see toDisplayHandicap.
const formatHandicap = (value) => formatHandicapText(value);

function PinGolfMyWeeks({ weeks, weekCount, dropCount }) {
  if (!weeks || weeks.length === 0) return null;

  // Which weeks actually count, by the same rule the season total uses: best (weekCount -
  // dropCount) point totals. A player looking at a bad week wants to know it was dropped.
  const counted = markCountedWeeks(
    weeks.map(week => week.weekly_points || 0),
    weekCount,
    dropCount
  );
  const anyPlayed = weeks.some(week => week.played);

  return (
    <div className="pg-my-weeks">
      <div className="pg-my-weeks-scroll">
        <table className="pg-my-weeks-table">
          <thead>
            <tr>
              <th>Week</th>
              <th>Card</th>
              <th>Hcp</th>
              <th>Net</th>
              <th>Place</th>
              <th>Pts</th>
            </tr>
          </thead>
          <tbody>
            {weeks.map((week, index) => {
              const isDropped = week.played && week.weekly_points > 0 && !counted[index];

              return (
                <tr
                  key={week.week_number}
                  className={[
                    week.played ? '' : 'is-unplayed',
                    isDropped ? 'is-dropped' : ''
                  ].filter(Boolean).join(' ')}
                >
                  <th scope="row">
                    <span className="pg-my-weeks-week">W{week.week_number}</span>
                    {week.provisional && <span className="pg-my-weeks-tag">live</span>}
                    {isDropped && <span className="pg-my-weeks-tag">dropped</span>}
                  </th>

                  {week.played ? (
                    <>
                      <td><PinGolfScore value={week.card_result} /></td>
                      <td>{formatHandicap(week.playing_handicap)}</td>
                      <td><PinGolfScore value={week.net} emphasis /></td>
                      <td>{week.placement ? `#${week.placement}` : '-'}</td>
                      <td>{week.weekly_points ?? '-'}</td>
                    </>
                  ) : (
                    <td className="pg-my-weeks-status" colSpan={5}>
                      {STATUS_LABELS[week.status] || 'Not played'}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="pg-my-weeks-note">
        {anyPlayed
          ? `Card is what you shot, Hcp is the strokes your handicap applied that week, and Net is the two added together - lower is better. Your handicap is the running average of the cards you finished. An unfinished week still counts here at its penalty, but it never changes your handicap. Best ${Math.max(weekCount - dropCount, 0)} of ${weekCount} weeks count toward your season total.`
          : 'Your cards will show up here week by week once you turn one in.'}
      </p>
    </div>
  );
}

export default PinGolfMyWeeks;
